/* eslint-disable @typescript-eslint/no-explicit-any */
import { navigateToUrl } from '@/utils/notify';
import { QueryClient, DefaultOptions, UseMutationOptions, QueryCache, MutationCache } from '@tanstack/react-query';
import { AxiosError } from 'axios';

export type ApiFnReturnType<FnType extends (...args: any) => Promise<any>> = Awaited<ReturnType<FnType>>;

export type QueryConfig<T extends (...args: any[]) => any> = Omit<ReturnType<T>, 'queryKey' | 'queryFn'>;

export type MutationConfig<MutationFnType extends (...args: any) => Promise<any>> = UseMutationOptions<
  ApiFnReturnType<MutationFnType>,
  Error,
  Parameters<MutationFnType>[0]
>;

/**
 * Custom mutation config that accepts error type as generic
 * This allows for proper typing of error callbacks without explicit type annotation
 */
export type CustomMutationConfig<MutationFnType extends (...args: any) => Promise<any>, TError = Error> = Omit<
  UseMutationOptions<Awaited<ReturnType<MutationFnType>>, TError, Parameters<MutationFnType>[0]>,
  'mutationFn'
>;

/**
 * Typed mutation config with automatic error typing
 * Use this when you want the onError callback to be automatically typed
 * Supports onMutate with context for optimistic updates
 */
export type TypedMutationConfig<
  MutationFnType extends (...args: any) => Promise<any>,
  TError = Error,
  TContext = unknown,
> = Omit<CustomMutationConfig<MutationFnType, TError>, 'onError' | 'onMutate'> & {
  onMutate?: (variables: Parameters<MutationFnType>[0]) => Promise<TContext> | TContext;
  onError?: (error: TError, variables: Parameters<MutationFnType>[0], context: TContext | undefined) => void;
};

/**
 * Key for localStorage for cache data
 */
const INITIAL_DATA_QUERY_KEY = 'queryDataNolimitCare';

const queryConfig: DefaultOptions = {
  queries: {
    // Smarter refetch: allow focus refetch except for heavy queries
    refetchOnWindowFocus: false,

    // Retry up to 2 times only for 500 errors
    retry: (failureCount, error: Error) => {
      // Check if error has status 500
      if (error instanceof AxiosError) {
        const status = error?.response?.status || error?.status;
        if (status === 500 && failureCount < 2) {
          return true;
        }
      }
      return false;
    },

    // Exponential backoff with jitter (aware of network type)
    retryDelay: (attemptIndex) => {
      const connection = (navigator as any)?.connection;
      const baseDelay = connection?.effectiveType === '4g' ? 1000 : 3000;
      return Math.min(baseDelay * 2 ** attemptIndex + Math.random() * 1000, 30_000);
    },

    // Freshness & caching (optimized for chat)
    staleTime: 30_000, // 30 seconds - chat data should be fresh
    gcTime: 30 * 60_000, // 30 min - keep chat history longer

    networkMode: 'online',
    refetchOnMount: true,
    refetchOnReconnect: true,
    structuralSharing: true,

    //  Keep previous data while refetching (good for lists)
    placeholderData: (prev: unknown) => prev,

    //  Optional: use persisted cache as initial data (localStorage/sessionStorage)
    initialData: () => {
      const cached = localStorage.getItem(INITIAL_DATA_QUERY_KEY);
      return cached ? JSON.parse(cached) : undefined;
    },
  },
  mutations: {
    retry: 1,
    networkMode: 'online',
    retryDelay: (i) => Math.min(1000 * 2 ** i, 5000),
  },
};

// Global error handler for 403 redirects
const handleGlobalError = (error: Error) => {
  if (error instanceof AxiosError) {
    const status = error?.response?.status || error?.status;
    if (status === 403) {
      navigateToUrl('/403');
    }
  }
};

// Create query client with optimized configuration
export const queryClient = new QueryClient({
  defaultOptions: queryConfig,

  // Enable query deduplication with global error handler
  queryCache: new QueryCache({
    onError: (error) => handleGlobalError(error),
  }),

  // Enable mutation deduplication with global error handler
  mutationCache: new MutationCache({
    onError: (error) => handleGlobalError(error),
  }),
});
